// Base URL resolution for CRA. Use REACT_APP_API_BASE_URL or default to /api (proxy/K8s)
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { getActivePortal, getAuthToken, refreshAccessToken, getBrowserFingerprint, clearAllSessions, performLogoutTeardown, isTokenExpired, getRefreshToken, getIsLoggingOut, hasAnyStoredToken, hasAnyStoredAuthToken } from '@/utils/auth';

const getApiBaseUrl = () => {
  const runtimeEnv = (typeof window !== 'undefined' && window.__ENV) ? window.__ENV.REACT_APP_API_BASE_URL : null;
  // Allow an explicit dev-server override for HTTPS tunnels. The bundled
  // env.js points native builds at staging and otherwise overrides this flag.
  const env = process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_BASE_URL || runtimeEnv
    : runtimeEnv || process.env.REACT_APP_API_BASE_URL;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalLikeHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname)
    || hostname.startsWith('192.168.')
    || hostname.startsWith('10.')
    || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
    || hostname.endsWith('.local');
  const isLocalDev = process.env.NODE_ENV === 'development'
    && isLocalLikeHost;
  if (isLocalDev) {
    return '/api';
  }
  if (env) {
    return env.endsWith('/api') ? env : `${env.replace(/\/$/, '')}/api`;
  }
  return '/api';
};

export const getCanonicalWebUrl = () => {
  // 1. Explicit environment configuration (runtime window.__ENV from container entrypoint or build-time process.env)
  const envUrl = (
    (typeof window !== 'undefined' && window.__ENV?.REACT_APP_WEB_URL) ||
    process.env.REACT_APP_WEB_URL ||
    ''
  ).trim();

  if (envUrl && !envUrl.includes('localhost') && !envUrl.startsWith('capacitor://')) {
    return envUrl.replace(/\/$/, '');
  }

  // 2. If running in a web browser (not Capacitor native and not localhost), use current window.location.origin
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    const isNative = (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) || origin.startsWith('capacitor://');
    if (!isNative && !origin.includes('localhost')) {
      return origin.replace(/\/$/, '');
    }
  }

  // 3. Fallback for native apps (Capacitor iOS/Android) when no env variable is set
  return 'https://app.budlee.ai';
};

const getWebUrl = () => {
  return getCanonicalWebUrl();
};

export const API_BASE_URL = getApiBaseUrl();
export const WEB_URL = getWebUrl();

export const apiUrl = (endpoint) => {
  const base = API_BASE_URL.replace(/\/$/, '');
  const clean = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${base}/${clean}`;
};

export const apiV1Url = (endpoint) => {
  const base = API_BASE_URL.replace(/\/api$/, '').replace(/\/$/, '');
  const clean = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${base}/api/v1/${clean}`;
};

/**
 * Ensures media URLs have valid percent-encoded path segments.
 * Fixes unencoded '?' in directory names (e.g. "how_do_organisms_reproduce?")
 * which cause standard browser URL parsers to truncate the path at the first '?'.
 */
export const sanitizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;

  let cleanUrl = url;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && cleanUrl.startsWith('http://')) {
    cleanUrl = cleanUrl.replace(/^http:\/\//i, 'https://');
  }

  const tokenIndex = cleanUrl.indexOf('?token=');
  if (tokenIndex !== -1) {
    const pathPart = cleanUrl.substring(0, tokenIndex);
    const queryPart = cleanUrl.substring(tokenIndex);
    const cleanPath = pathPart.replace(/\?/g, '%3F');
    return `${cleanPath}${queryPart}`;
  }

  const extMatch = cleanUrl.match(/(\.(?:mp4|mkv|mov|avi|wmv|mp3|wav|m4a|flac|ogg|pdf|png|jpg|jpeg|webp)\?)(.*)/i);
  if (extMatch) {
    const extWithQuestion = extMatch[1];
    const queryString = extMatch[2];
    const pathBeforeExt = cleanUrl.substring(0, cleanUrl.indexOf(extWithQuestion) + extWithQuestion.length - 1);
    const cleanPath = pathBeforeExt.replace(/\?/g, '%3F');
    return `${cleanPath}?${queryString}`;
  }

  const firstQuestion = cleanUrl.indexOf('?');
  const lastQuestion = cleanUrl.lastIndexOf('?');
  if (firstQuestion !== -1 && firstQuestion !== lastQuestion) {
    const pathPart = cleanUrl.substring(0, lastQuestion).replace(/\?/g, '%3F');
    const queryPart = cleanUrl.substring(lastQuestion + 1);
    return `${pathPart}?${queryPart}`;
  }

  return cleanUrl;
};

export const mediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return sanitizeMediaUrl(path);
  const clean = path.startsWith('/') ? path.slice(1) : path;
  // Base URL is usually API_BASE_URL minus the /api suffix
  let baseUrl = API_BASE_URL.replace(/\/api$/, '').replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
    baseUrl = baseUrl.replace(/^http:\/\//i, 'https://');
  }
  return sanitizeMediaUrl(`${baseUrl}/${clean}`);
};

// Setup axios interceptors for fingerprint and auth
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Resets interceptor queue and aborts pending token refresh promises on logout
 */
export const resetAuthInterceptorQueue = () => {
  isRefreshing = false;
  failedQueue.forEach(prom => {
    try {
      prom.reject(new Error('Auth request queue cancelled due to session logout.'));
    } catch (_) {}
  });
  failedQueue = [];
};

// Automatically listen for logout events to clear interceptor queues
if (typeof window !== 'undefined') {
  window.addEventListener('sessionLoggedOut', () => {
    resetAuthInterceptorQueue();
  });
}

/**
 * Apply fingerprint and auth interceptors to an axios instance
 */
export const applyInterceptors = (instance) => {
  if (!instance || !instance.interceptors) return;

  // Request interceptor: Add fingerprint header and auth token
  instance.interceptors.request.use(
    async (config) => {
      // Add fingerprint header for all requests
      try {
        const fingerprintId = await getBrowserFingerprint();
        if (fingerprintId) {
          config.headers['X-Fingerprint-ID'] = fingerprintId;
        }
      } catch (error) {
        console.warn('Failed to get fingerprint:', error);
      }

      // If logging out, do not attach stale tokens or trigger background refreshes
      if (getIsLoggingOut()) {
        return config;
      }

      // Add auth token if available
      const activePortal = config.portal || getActivePortal();
      if (activePortal && !config.headers['Authorization']) {
        let token = getAuthToken(activePortal);
        if (token) {
          const isRefreshRequest = config.url && config.url.includes('/users/token/refresh/');
          // Preemptive token expiry check (Frontend) - exclude the refresh request itself and explicit skips
          if (!isRefreshRequest && !config.skipAuthRefresh && !getIsLoggingOut() && isTokenExpired(token) && getRefreshToken(activePortal)) {
            try {
              token = await refreshAccessToken(activePortal);
            } catch (refreshErr) {
              console.error('[Preemptive Refresh Fail]', refreshErr);
              const isExplicitAuthFailure = refreshErr.response?.status === 401 || refreshErr.response?.status === 400;
              if (isExplicitAuthFailure && hasAnyStoredToken()) {
                performLogoutTeardown('/#/login');
                return Promise.reject(refreshErr);
              }
            }
          }
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle 401 (refresh token) and 402 (payment required)
  instance.interceptors.response.use(
    (response) => {
      // Check for rate limit headers in successful responses
      const limit = parseInt(response.headers?.['x-ratelimit-limit']);
      const used = parseInt(response.headers?.['x-ratelimit-used']);
      const feature = response.headers?.['x-ratelimit-feature'];

      if (!isNaN(limit) && !isNaN(used) && limit > 0) {
        const usageRatio = used / limit;
        if (usageRatio >= 0.75) {
          // Dispatch warning for 75%+ limit hit
          window.dispatchEvent(new CustomEvent('rateLimitWarning', {
            detail: {
              feature: (feature || 'this feature').toLowerCase(),
              limit: limit,
              used: used,
              remaining: limit - used
            }
          }));
        }
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const requestUrl = originalRequest?.url || '';
      const isAuthEndpoint = requestUrl.includes('/users/login/') ||
                             requestUrl.includes('/users/signup/') ||
                             requestUrl.includes('/users/google/') ||
                             requestUrl.includes('/users/apple/') ||
                             requestUrl.includes('/users/password-reset/') ||
                             requestUrl.includes('/users/verify-reset-code/') ||
                             requestUrl.includes('/users/set-new-password/') ||
                             requestUrl.includes('/users/resolve-device-limit/') ||
                             requestUrl.includes('/users/token/refresh/');

      // Handle 401 - Token expired, try to refresh
      if (error.response?.status === 401) {
        // Do not try to refresh if request is an auth endpoint, if skipAuthRefresh is set, or if user is logging out
        if (isAuthEndpoint || originalRequest?.skipAuthRefresh || getIsLoggingOut()) {
          return Promise.reject(error);
        }

        // Special case: Session was revoked (by limit or manually)
        // Check both direct response and nested data for the code
        const responseData = error.response.data;
        const errorCode = responseData?.code || (responseData?.detail?.code);
        const errorDetail = typeof responseData?.detail === 'string' ? responseData.detail : (typeof responseData?.error === 'string' ? responseData.error : '');
        const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
        const hasTokens = hasAnyStoredToken() || hasAnyStoredAuthToken();

        if (errorCode === 'session_revoked') {
          window.dispatchEvent(new CustomEvent('sessionRevoked', { 
            detail: typeof responseData.detail === 'object' ? responseData.detail : responseData 
          }));
          
          // CRITICAL: Un-stick the app if revoked during startup
          performLogoutTeardown('/#/login');
          return Promise.reject(error);
        }

        // Fast-track: User account was deleted or marked inactive in backend
        const isDeadUser = errorCode === 'user_not_found' ||
                           errorCode === 'user_deleted' ||
                           errorCode === 'user_inactive' ||
                           errorDetail.toLowerCase().includes('user not found') ||
                           errorDetail.toLowerCase().includes('user is inactive') ||
                           errorDetail.toLowerCase().includes('user account has been deleted');

        if (!isActuallyGuest && isDeadUser) {
          console.warn('🔴 User account no longer exists or is inactive. Tearing down session immediately.');
          performLogoutTeardown('/#/login');
          return Promise.reject(error);
        }

        if (originalRequest._retry) {
          if (!isActuallyGuest && hasTokens) {
            console.error('🔴 Request failed with 401 even after retry/refresh. Tearing down session.');
            performLogoutTeardown('/#/login');
          }
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Queue the request while token is being refreshed
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const activePortal = originalRequest.portal || getActivePortal();
          if (activePortal) {
            const newToken = await refreshAccessToken(activePortal);
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // --- MULTI-TAB RACE CONDITION & SOFT-FAIL MITIGATION ---
          const activePortal = originalRequest.portal || getActivePortal();
          if (activePortal) {
            const currentToken = getAuthToken(activePortal);
            if (currentToken && originalRequest.headers['Authorization'] !== `Bearer ${currentToken}` && !isTokenExpired(currentToken)) {
              console.log('✅ Recovered from refresh race condition (new token found in storage)');
              processQueue(null, currentToken);
              originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
              return axios(originalRequest);
            }
          }

          // Delayed check (500ms) for multi-tab synchronization before clearing sessions
          await new Promise(resolve => setTimeout(resolve, 500));
          if (activePortal) {
            const currentToken = getAuthToken(activePortal);
            if (currentToken && !isTokenExpired(currentToken)) {
              console.log('✅ Recovered from refresh race condition after 500ms delay');
              processQueue(null, currentToken);
              originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
              return axios(originalRequest);
            }
          }

          processQueue(refreshError, null);

          // Soft Fail: ONLY clear session and redirect if refresh was explicitly rejected by backend (400/401)
          // Do NOT clear sessions for network timeouts, offline states, or server 5xx errors!
          const isExplicitAuthFailure = refreshError.response?.status === 401 || refreshError.response?.status === 400;
          const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
          const hasAnyToken = hasAnyStoredToken();

          if (hasAnyToken && isExplicitAuthFailure) {
              // Real user whose refresh token was explicitly revoked/expired by backend
              console.error('🔴 Unrecoverable 401 error. Clearing session and redirecting to login.');
              performLogoutTeardown('/#/login');
              return Promise.reject(refreshError);
          } else if (isActuallyGuest) {
              window.dispatchEvent(new CustomEvent('paymentRequired', {
                  detail: {
                    feature: 'this feature',
                    message: 'Please sign up or login to continue.',
                    statusCode: 401
                  }
              }));
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Unified Guest Detection for Rate Limiting / Payment Required
      const checkIsGuest = () => {
        if (typeof window === 'undefined') return false;

        // Explicit guest session flag
        if (localStorage.getItem('is_guest_session') === 'true') return true;

        // Check for any valid auth tokens across portals
        const hasAuthToken = hasAnyStoredAuthToken();

        return !hasAuthToken;
      };

      const isGuestUser = checkIsGuest();
      const url = originalRequest?.url || '';
      const isSubscriptionUrl = Boolean(url && (url.includes('stripe') || url.includes('subscription') || url.includes('pricing')));

      // Handle 402 - Payment required (guest limit exceeded - feature-based)
      if (error.response?.status === 402 && !isSubscriptionUrl) {
        const isActuallyGuest = isGuestUser;
        const message = isActuallyGuest
          ? 'You have already hit your guest account limits. Sign up for more limits.'
          : 'You have already hit your free account limits';

        window.dispatchEvent(new CustomEvent('paymentRequired', {
          detail: {
            feature: error.response.data?.feature || 'this feature',
            message: message,
            remaining: error.response.data?.remaining,
            max_count: error.response.data?.max_count,
            statusCode: 402,
            user_type: error.response.data?.user_type,
            isFreeUser: error.response.data?.user_type === 'free' || error.response.data?.is_free_user || !isActuallyGuest
          }
        }));
        // Suppress specific toast messaging for these status codes by returning a custom error
        return Promise.resolve({ data: { message: 'HANDLED_LIMIT' } });
      }

      // Handle 429 - Too Many Requests (DRF throttling - guest limit exceeded)
      if (error.response?.status === 429 && !isSubscriptionUrl) {
        const isActuallyGuest = isGuestUser;
        const message = isActuallyGuest
          ? 'You have already hit your guest account limits. Sign up for more limits.'
          : 'You have already hit your free account limits';

        // Dispatch paymentRequired for both guest and authenticated (free) users
        window.dispatchEvent(new CustomEvent('paymentRequired', {
          detail: {
            feature: error.response.data?.feature || (isActuallyGuest ? 'Guest Limit' : 'API Limit'),
            message: message,
            remaining: 0,
            statusCode: 429,
            user_type: isActuallyGuest ? 'guest' : 'free',
            isFreeUser: !isActuallyGuest
          }
        }));
        // Suppress specific toast messaging for these status codes by returning a custom error
        return Promise.resolve({ data: { message: 'HANDLED_LIMIT' } });
      }

      // Handle 401 - Unauthorized (fall-through / unrecoverable)
      if (error.response?.status === 401) {
          const hasAnyToken = hasAnyStoredToken();
          
          if (hasAnyToken) {
              console.error('🔴 Unrecoverable 401/Unauthorized error. Redirecting to login.');
              performLogoutTeardown('/#/login');
              return Promise.reject(error);
          }
      }

      return Promise.reject(error);
    }
  );
};

// Apply to global axios as well
applyInterceptors(axios);
